import React, { useState, useEffect } from 'react';
import { Box, Chip, CircularProgress } from '@mui/material';
import { CheckCircle, Cloud, Error } from '@mui/icons-material';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';

/**
 * Auto-save indicator component
 * Shows saving status with icon and text
 */
const AutoSaveIndicator = ({ isFormCreated }) => {
  const [status, setStatus] = useState('saved'); // 'saving', 'saved', 'error'
  const [lastSavedTime, setLastSavedTime] = useState(null);

  useEffect(() => {
    if (!isFormCreated) {
      setStatus('saved');
      return;
    }

    // Listen for custom auto-save events
    const handleSaving = () => setStatus('saving');
    const handleSaved = () => {
      setStatus('saved');
      setLastSavedTime(toLocalISO());
    };
    const handleError = () => setStatus('error');

    window.addEventListener('autosave:saving', handleSaving);
    window.addEventListener('autosave:saved', handleSaved);
    window.addEventListener('autosave:error', handleError);

    return () => {
      window.removeEventListener('autosave:saving', handleSaving);
      window.removeEventListener('autosave:saved', handleSaved);
      window.removeEventListener('autosave:error', handleError);
    };
  }, [isFormCreated]);

  if (!isFormCreated) {
    return null;
  }

  const getStatusConfig = () => {
    switch (status) {
      case 'saving':
        return {
          icon: <CircularProgress size={16} sx={{ color: '#1976d2' }} />,
          label: 'Saving...',
          color: 'primary',
        };
      case 'saved':
        return {
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
          label: lastSavedTime ? `Saved at ${new Date(lastSavedTime).toLocaleTimeString()}` : 'All changes saved',
          color: 'success',
        };
      case 'error':
        return {
          icon: <Error sx={{ fontSize: 16 }} />,
          label: 'Save failed',
          color: 'error',
        };
      default:
        return {
          icon: <Cloud sx={{ fontSize: 16 }} />,
          label: 'Auto-save enabled',
          color: 'default',
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Box sx={{ marginRight: 'auto', zIndex: 1000 }}>
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        sx={{
          boxShadow: 2,
          '& .MuiChip-icon': {
            marginLeft: '8px',
          },
        }}
      />
    </Box>
  );
};

export default AutoSaveIndicator;
